import optimizelySdk, {
  Client,
  enums as optimizelyEnums,
  OptimizelyDecision,
  OptimizelyUserContext,
} from '@optimizely/optimizely-sdk';
import { useEffect, useMemo, useState } from 'react';

import useQueryParams from '@/hooks/useQueryParams';
import { segmentDecision } from '@/services/analytics/trackEventV2';
import { getCookie, setCookie } from '@/utility/cookie';
import { makeVar, useReactiveVar } from '@/utility/reactiveVariable';

export * from './flags';
declare global {
  interface Window {
    optimizelyClientInstance: Client;
  }
}

const OPTIMIZELY_TIMEOUT = 5_000;

export const ENABLED_VARIATION_KEY = 'enabled';
const ON_VARIATION_KEY = 'on';
const DEFAULT_ENABLED_VARIATION_KEYS = [ENABLED_VARIATION_KEY, ON_VARIATION_KEY];

// OptimizelyDecision types are refined into ActiveOptimizelyDecision.
export interface ActiveOptimizelyDecision extends OptimizelyDecision {
  enabled: true;
}

const isActiveDecision = (
  decision: Partial<OptimizelyDecision>,
): decision is ActiveOptimizelyDecision => {
  return !!decision.enabled;
};

const isOptimizelyReady = makeVar(false);
const optimizelyUser = makeVar<OptimizelyUserContext | null>(null);

// This function is used to set the user context for Optimizely.
// If Optimizely is not ready, it will set the user context when Optimizely is ready.
// Calling the function again while Optimizely is not ready will cancel the previous
// request, and the new one will take its place.
export const setOptimizelyUser = (() => {
  let unsubscribe: undefined | (() => void) = undefined;

  return (userId: string, attributes: Record<string, unknown> = {}) => {
    const update = () => {
      unsubscribe?.();
      unsubscribe = undefined;

      if (isOptimizelyReady()) {
        optimizelyUser(window.optimizelyClientInstance.createUserContext(userId, attributes));
        return true;
      }

      return false;
    };

    !update() && (unsubscribe = isOptimizelyReady.onNextChange(update));
  };
})();

const useOptimizelyUser = () => useReactiveVar(optimizelyUser);

const setupOptimizely = async (isEnabled: boolean, isLoggerEnabled: boolean) => {
  const sdkKey = process.env.optimizelySDKKey;
  if (!isEnabled || !sdkKey) return;
  const optimizelyClientInstance = optimizelySdk.createInstance({
    logger: isLoggerEnabled
      ? undefined
      : {
          log() {
            // noop
          },
        },
    sdkKey,
  });
  // This registers the optimizely client instance on the window for segment to detect.
  // See: https://segment.com/docs/connections/destinations/catalog/optimizely-web/#optimizely-full-stack-javascript-sdk
  window.optimizelyClientInstance = optimizelyClientInstance;
  const { success } = await optimizelyClientInstance.onReady({ timeout: OPTIMIZELY_TIMEOUT });
  if (success) {
    optimizelyClientInstance.notificationCenter.addNotificationListener(
      optimizelyEnums.NOTIFICATION_TYPES.DECISION,
      segmentDecision,
    );
    isOptimizelyReady(true);
  }
};

type OptimizelyDecisionOverride = Omit<OptimizelyDecision, 'userContext'>;

// Safe JSON parsing with reviver to prevent prototype pollution
const safeJSONParse = (jsonString: string) => {
  return JSON.parse(jsonString, (key, value) => {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return undefined;
    }
    return value;
  });
};

// Singleton cache for cookie overrides
const cookieOverridesCache = (() => {
  let lastCookieValue: string | undefined = undefined;
  let cachedOverrides: Record<string, OptimizelyDecisionOverride> = {};

  return (currentCookie: string | undefined) => {
    if (currentCookie !== lastCookieValue) {
      lastCookieValue = currentCookie;

      if (!currentCookie) {
        cachedOverrides = {};
      } else {
        try {
          cachedOverrides = safeJSONParse(currentCookie);
        } catch {
          cachedOverrides = {};
        }
      }
    }
    return cachedOverrides;
  };
})();

// Singleton cache for experiment override params
const experimentOverrideParamsCache = (() => {
  let lastParamsHash: string | undefined = undefined;
  let cachedParams: Record<string, OptimizelyDecisionOverride> = {};

  return (params: Record<string, unknown>) => {
    // Filter matching params first
    const relevantParams = Object.entries(params).filter(
      ([key]) => key.startsWith('od-exp[') && key.endsWith(']'),
    );

    // Use simple string literal for empty case to avoid JSON.stringify
    const paramsHash = relevantParams.length === 0 ? '[]' : JSON.stringify(relevantParams.sort());

    if (paramsHash !== lastParamsHash) {
      lastParamsHash = paramsHash;

      const experimentOverrideParams: Record<string, OptimizelyDecisionOverride> = {};

      // Only process if we have relevant params
      if (relevantParams.length > 0) {
        relevantParams.forEach(([key, value]) => {
          const experimentKey = key.slice(7, -1);
          const decision = {
            enabled: true,
            variationKey: 'enabled',
            flagKey: experimentKey,
            variables: {},
            reasons: [],
            ruleKey: '',
          };

          if (value === 'true') {
            experimentOverrideParams[experimentKey] = decision;
          } else if (value === 'false') {
            decision.enabled = false;
            experimentOverrideParams[experimentKey] = decision;
          } else if (typeof value === 'string') {
            try {
              const json = atob(value);
              const data = safeJSONParse(json);

              experimentOverrideParams[experimentKey] = { ...decision, ...data };
            } catch {}
          }
        });
      }

      cachedParams = experimentOverrideParams;
    }
    return cachedParams;
  };
})();

// Global cache for final experiment overrides
const experimentOverridesCache = (() => {
  let lastOverridesHash: string | undefined = undefined;
  let cachedOverrides: Record<string, OptimizelyDecisionOverride> = {};

  return (
    cookieOverrides: Record<string, OptimizelyDecisionOverride>,
    experimentOverrideParams: Record<string, OptimizelyDecisionOverride>,
  ) => {
    // Create a hash of the combined overrides
    const overridesHash = JSON.stringify([cookieOverrides, experimentOverrideParams]);

    if (overridesHash !== lastOverridesHash) {
      lastOverridesHash = overridesHash;

      const newOverrides = { ...cookieOverrides, ...experimentOverrideParams };
      cachedOverrides = newOverrides;

      // Set the cookie here, only when overrides actually change
      setCookie('od-exp', JSON.stringify(newOverrides));
    }
    return cachedOverrides;
  };
})();

/**
 * Hook to parse experiment override parameters from URL query string.
 * Supports format: od-exp[EXPERIMENT_KEY]=OVERRIDE_VALUE
 * - OVERRIDE_VALUE can be 'true', 'false', or base64-encoded JSON
 * - Automatically persists overrides to cookies for navigation persistence
 */
function useExperimentOverrides() {
  const params = useQueryParams();
  const cookie = getCookie('od-exp');

  const cookieOverrides = cookieOverridesCache(cookie);
  const experimentOverrideParams = experimentOverrideParamsCache(params);

  return experimentOverridesCache(cookieOverrides, experimentOverrideParams);
}

/**
 * Use a potentially inactive optimizely decision. decision.enabled may be false here.
 * This will return the decision even if it is not in the active state.
 */
const useRawOptimizelyDecision = (
  flagKey: string,
  shouldRun: boolean,
): OptimizelyDecision | null => {
  const user = useOptimizelyUser();
  const flag = useMemo((): OptimizelyDecision | null => {
    const userId = user?.getUserId();
    if (shouldRun && user && userId) {
      const decision = user?.decide(flagKey, []);
      return decision;
    }
    return null;
  }, [user, flagKey, shouldRun]);

  return flag;
};

/**
 * Use an active optimizely decision for a given flagKey.
 * Note that if the decision is not in the "enabled" state, this will return null.
 * "Enabled" not to be confused with the "enabled" variant.
 */
export const useOptimizelyDecision = (
  flagKey: string,
  shouldRun: boolean = true,
): ActiveOptimizelyDecision | null => {
  const overrides = useExperimentOverrides();
  const decision = useRawOptimizelyDecision(flagKey, shouldRun);

  const experimentOverride = overrides[flagKey];
  if (experimentOverride) {
    return isActiveDecision(experimentOverride) ? experimentOverride : null;
  }

  if (!decision) return null;

  return isActiveDecision(decision) ? decision : null;
};

export const useOptimizelyDecisionTimedOut = (
  flagKey: string,
  shouldRun: boolean = true,
  timeoutMs: number = 1000,
): { isLoaded: boolean; decision: ActiveOptimizelyDecision | null } => {
  const [timedOut, setTimedOut] = useState(false);
  const overrides = useExperimentOverrides();
  const decision = useRawOptimizelyDecision(flagKey, shouldRun);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setTimedOut(true);
    }, timeoutMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [timeoutMs]);

  const experimentOverride = overrides[flagKey];
  if (experimentOverride) {
    return isActiveDecision(experimentOverride)
      ? {
          isLoaded: true,
          decision: experimentOverride,
        }
      : {
          isLoaded: !!experimentOverride || timedOut,
          decision: null,
        };
  }

  if (!decision) return { isLoaded: timedOut, decision: null };

  return isActiveDecision(decision)
    ? { isLoaded: true, decision }
    : { isLoaded: !!decision || timedOut, decision: null };
};

export const useExperimentIsEnabled = (
  flagKey: string,
  opts?: {
    enabledVariationKeys?: string[];
    shouldRun?: boolean;
  },
) => {
  const { enabledVariationKeys = DEFAULT_ENABLED_VARIATION_KEYS, shouldRun = true } = opts ?? {};
  const decision = useOptimizelyDecision(flagKey, shouldRun);
  return enabledVariationKeys.includes(decision?.variationKey ?? '');
};

export default setupOptimizely;
